'use client';

import { useEffect, useRef } from 'react';
import { useExportRequests } from 'src/hooks/query/useExportRequests';
import useAppEventEmitter from 'src/hooks/useAppEventEmitter';
import useExportsSessionStore from 'src/stores/useExportsSessionStore';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';

/**
 * App-level driver for export downloads. Mounting it keeps the export-requests
 * query polling in the background (React Query dedupes with the exports table,
 * which uses the same query key) and emits a `downloadReady` event the moment a
 * previously-pending job gains a download URL. The event is picked up by the
 * alert-notification listener to surface the "Download ready!" toast.
 *
 * The query stays dormant until the user requests an export this session.
 * Otherwise it would fetch — and prompt a wallet signature — on every app load,
 * since the data API endpoint is signature-gated.
 */
const useDownloadRequests = () => {
	const authorityStr = useVelocityStore((s) =>
		s.wallet.current.adapter?.publicKey?.toBase58()
	);
	const requestedExportAuthority = useExportsSessionStore(
		(s) => s.requestedExportAuthority
	);
	const hasRequestedExport =
		!!authorityStr && requestedExportAuthority === authorityStr;
	const { data } = useExportRequests({ enabled: hasRequestedExport });
	const appEventEmitter = useAppEventEmitter();

	// Keys of jobs already seen as available, scoped to the authority they were
	// seeded for. Seeded on the first successful fetch (without notifying), so
	// only jobs that *become* ready this session trigger a toast. Scoping by
	// authority stops a wallet switch from comparing the new authority's ready
	// jobs against the previous authority's keys and firing a false toast.
	const seenAvailableKeys = useRef<{
		authority: string | undefined;
		keys: Set<string>;
	} | null>(null);

	useEffect(() => {
		if (!data) return;

		const availableKeys = new Set(
			data.filter((file) => file.downloadUrl).map((file) => file.key)
		);

		if (seenAvailableKeys.current?.authority === authorityStr) {
			const hasNewlyAvailable = [...availableKeys].some(
				(key) => !seenAvailableKeys.current?.keys.has(key)
			);

			if (hasNewlyAvailable) {
				appEventEmitter.emit('downloadReady');
			}
		}

		seenAvailableKeys.current = {
			authority: authorityStr,
			keys: availableKeys,
		};
	}, [data, appEventEmitter, authorityStr]);
};

export default useDownloadRequests;
